import type { FullWeaponInfo, ProgressionInfo, WeaponId, WeaponProgress } from "../../../api/types";
import { weaponName } from "../../../state/meta";
import type { MetaIndex } from "../../../state/meta";
import { LockBadge } from "../../components/Badge";
import { Progress } from "../../components/Progress";
import { formatInt } from "../../format";
import { nextWeaponUnlock, weaponLevelProgress, weaponRows } from "./profileStats";
import type { WeaponUnlockHint } from "./profileStats";

const HINT_KIND_LABEL: Record<WeaponUnlockHint["kind"], string> = {
  attachment: "Attachment",
  camo: "Camo",
};

export interface WeaponLevelsProps {
  weaponProgress: readonly WeaponProgress[];
  unlockedWeapons: readonly WeaponId[];
  progression: ProgressionInfo;
  index: MetaIndex;
}

type HintNames = Record<WeaponUnlockHint["kind"], ReadonlyMap<string, { name: string }>>;

interface WeaponRowProps {
  row: WeaponProgress;
  info: FullWeaponInfo | undefined;
  progression: ProgressionInfo;
  hintNames: HintNames;
}

function WeaponRow({ row, info, progression, hintNames }: WeaponRowProps) {
  const name = weaponName(row.weapon);
  const progress = weaponLevelProgress(row.xp, row.level, progression.weaponLevelXp);
  const isMax = progress.forNext === 0;
  const hint = info ? nextWeaponUnlock(info, row.level, progression.camoLevels) : null;

  return (
    <li class="profile__weapon">
      <div class="profile__weapon-head">
        <span class="profile__weapon-name">{name}</span>
        {info && <span class="profile__weapon-class">{info.class}</span>}
        <span class="profile__weapon-level">
          <span class="eyebrow" aria-hidden="true">
            Lv
          </span>
          <span class="sr-only">Level </span>
          <span class="lcd" data-ghost="88">
            {row.level}
          </span>
        </span>
      </div>
      <Progress
        value={progress.intoLevel}
        max={progress.forNext}
        tone={isMax ? "amber" : "cyan"}
        label={`${name} level progress`}
      />
      <div class="profile__weapon-meta num">
        {isMax ? (
          <span>
            <strong class="profile__max">Max</strong> <span class="muted">{formatInt(row.xp)} XP</span>
          </span>
        ) : (
          <span>
            {formatInt(progress.intoLevel)} / {formatInt(progress.forNext)} XP
          </span>
        )}
        <span>
          {formatInt(row.kills)} kills · {formatInt(row.headshots)} headshots
        </span>
      </div>
      <div class="profile__weapon-next">
        {hint ? (
          <>
            <span class="eyebrow">Next</span>
            <LockBadge level={hint.level} weapon />
            <span class="profile__weapon-next-name">{hintNames[hint.kind].get(hint.id)?.name ?? hint.id}</span>
            <span class="profile__weapon-next-kind">{HINT_KIND_LABEL[hint.kind]}</span>
          </>
        ) : (
          info && <span class="muted">Everything unlocked</span>
        )}
      </div>
    </li>
  );
}

/** One row per weapon, most XP first, each with its level progress and the next unlock. */
export function WeaponLevels({ weaponProgress, unlockedWeapons, progression, index }: WeaponLevelsProps) {
  const rows = weaponRows(weaponProgress, unlockedWeapons);
  const hintNames: HintNames = { attachment: index.attachments, camo: index.camos };
  return (
    <section class="profile__panel" aria-labelledby="profile-weapons-title">
      <header class="profile__panel-head">
        <h2 id="profile-weapons-title" class="profile__h">
          Weapon levels
        </h2>
        <span class="eyebrow">{rows.length} weapons</span>
      </header>
      <div class="profile__scroll" tabIndex={0} role="region" aria-labelledby="profile-weapons-title">
        {rows.length === 0 ? (
          <p class="muted">No weapons unlocked yet.</p>
        ) : (
          <ul class="profile__weapons">
            {rows.map((row) => (
              <WeaponRow
                key={row.weapon}
                row={row}
                info={index.weapons.get(row.weapon)}
                progression={progression}
                hintNames={hintNames}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
